import React from 'react'

import AUTH_CONFIG from '@shared/lib/constants/auth-config'
import { LocalStorage } from '@shared/services/main/storage'
import { emitAuthChanged } from './auth-events'
import { platformGetCurrentUser } from './api'
import { PLATFORM_AUTH_STORAGE_KEYS } from './storage'

const HOST_BALANCE_CHANGED_EVENT = 'c7:auth:balance-changed'

function extractCreditFromUser(user) {
  const balance = user?.balance ?? user?.wallet?.balance ?? null
  if (balance == null) return null
  const n = Number(balance)
  return Number.isFinite(n) ? n : null
}

function mergeSessionFieldsIntoUser(user, session) {
  const mergedUser = user && typeof user === 'object' ? { ...user } : {}
  if (session && typeof session === 'object') {
    if (session.balance !== undefined) mergedUser.balance = session.balance
    if (session.wallet !== undefined) mergedUser.wallet = session.wallet
    if (session.player_id !== undefined) mergedUser.player_id = session.player_id
    if (session.prepaid_wallet_id !== undefined) mergedUser.prepaid_wallet_id = session.prepaid_wallet_id
    if (session.timezone !== undefined) mergedUser.timezone = session.timezone
    if (session.odds_format !== undefined) mergedUser.odds_format = session.odds_format
  }
  return Object.keys(mergedUser).length ? mergedUser : null
}

function normalizeAuthPayload(payload) {
  const user = mergeSessionFieldsIntoUser(payload?.user || null, payload?.session || null)
  const sessionId = payload?.session_id || null
  const apiToken = payload?.api_token || payload?.user?.api_token || null
  const credit = extractCreditFromUser(user)

  return {
    user,
    sessionId,
    apiToken,
    credit,
  }
}

function persistAuthState({ user, sessionId, apiToken, credit }) {
  LocalStorage.set(PLATFORM_AUTH_STORAGE_KEYS.USER, user)
  if (apiToken) LocalStorage.set(PLATFORM_AUTH_STORAGE_KEYS.API_TOKEN, apiToken)
  else LocalStorage.remove(PLATFORM_AUTH_STORAGE_KEYS.API_TOKEN)
  if (sessionId) LocalStorage.set(PLATFORM_AUTH_STORAGE_KEYS.SESSION_ID, sessionId)
  else LocalStorage.remove(PLATFORM_AUTH_STORAGE_KEYS.SESSION_ID)
  if (credit != null) LocalStorage.set(PLATFORM_AUTH_STORAGE_KEYS.BALANCE, credit)
  else LocalStorage.remove(PLATFORM_AUTH_STORAGE_KEYS.BALANCE)
}

function clearPersistedAuthState() {
  LocalStorage.remove(PLATFORM_AUTH_STORAGE_KEYS.USER)
  LocalStorage.remove(PLATFORM_AUTH_STORAGE_KEYS.API_TOKEN)
  LocalStorage.remove(PLATFORM_AUTH_STORAGE_KEYS.BALANCE)
  LocalStorage.remove(PLATFORM_AUTH_STORAGE_KEYS.SESSION_ID)
}

const AuthContext = React.createContext(null)

export function useAuth() {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

function getInitialAuthState() {
  const user = LocalStorage.get(PLATFORM_AUTH_STORAGE_KEYS.USER) || null
  const sessionId = LocalStorage.get(PLATFORM_AUTH_STORAGE_KEYS.SESSION_ID) || null
  const storedBalance = LocalStorage.get(PLATFORM_AUTH_STORAGE_KEYS.BALANCE)
  const credit = storedBalance != null ? Number(storedBalance) : null
  return {
    user,
    sessionId,
    credit,
    isLoading: true,
  }
}

export default function AuthProvider({ children }) {
  const [state, setState] = React.useState(getInitialAuthState)

  const applyAuthPayload = React.useCallback((payload) => {
    const nextState = normalizeAuthPayload(payload)
    setState({
      user: nextState.user,
      sessionId: nextState.sessionId,
      credit: nextState.credit,
      isLoading: false,
    })
    persistAuthState(nextState)
    emitAuthChanged({
      user: nextState.user,
      session_id: nextState.sessionId,
      credit: nextState.credit,
    })
    return nextState
  }, [])

  const applyBalanceChange = React.useCallback((nextBalance) => {
    const parsedBalance = Number(nextBalance)
    if (!Number.isFinite(parsedBalance)) return

    setState(prev => {
      if (!prev.user) return prev

      const nextUser = {
        ...prev.user,
        balance: parsedBalance,
        wallet: prev.user.wallet && typeof prev.user.wallet === 'object'
          ? { ...prev.user.wallet, balance: parsedBalance }
          : prev.user.wallet,
      }
      const nextState = {
        user: nextUser,
        sessionId: prev.sessionId,
        credit: parsedBalance,
        isLoading: false,
      }

      persistAuthState({
        user: nextUser,
        sessionId: prev.sessionId,
        apiToken: LocalStorage.get(PLATFORM_AUTH_STORAGE_KEYS.API_TOKEN) || null,
        credit: parsedBalance,
      })
      emitAuthChanged({
        user: nextUser,
        session_id: prev.sessionId,
        credit: parsedBalance,
      })
      return nextState
    })
  }, [])

  const sync = React.useCallback(async (signal) => {
    try {
      const payload = await platformGetCurrentUser()
      if (signal?.cancelled) return
      applyAuthPayload(payload)
    } catch (err) {
      if (signal?.cancelled) return
      const status = err?.response?.status
      if (status === 401 || status === 403) {
        setState({ user: null, sessionId: null, credit: null, isLoading: false })
        clearPersistedAuthState()
        emitAuthChanged({ user: null, session_id: null, credit: null })
      } else {
        setState(prev => prev.isLoading ? { ...prev, isLoading: false } : prev)
      }
    }
  }, [applyAuthPayload])

  React.useEffect(() => {
    const signal = { cancelled: false }
    sync(signal)

    const intervalSec = Number(AUTH_CONFIG.CHECK_INTERVAL)
    const intervalMs = Number.isFinite(intervalSec) && intervalSec > 0 ? intervalSec * 1000 : null
    let id
    if (intervalMs) {
      id = window.setInterval(() => sync(signal), intervalMs)
    }
    return () => {
      signal.cancelled = true
      if (id) window.clearInterval(id)
    }
  }, [sync])

  const refreshAuth = React.useCallback(() => sync({ cancelled: false }), [sync])

  React.useEffect(() => {
    const handler = () => {
      LocalStorage.remove(PLATFORM_AUTH_STORAGE_KEYS.SESSION_ID)
      refreshAuth()
    }
    window.addEventListener('c7:auth:session-invalid', handler)
    return () => window.removeEventListener('c7:auth:session-invalid', handler)
  }, [refreshAuth])

  React.useEffect(() => {
    const handler = (event) => {
      const nextBalance = event?.detail?.balance
      applyBalanceChange(nextBalance)
      refreshAuth()
    }
    window.addEventListener(HOST_BALANCE_CHANGED_EVENT, handler)
    return () => window.removeEventListener(HOST_BALANCE_CHANGED_EVENT, handler)
  }, [applyBalanceChange, refreshAuth])

  const openLoginModal = React.useCallback((mode = 'login') => {
    window.dispatchEvent(new CustomEvent('c7:auth:open', { detail: { mode } }))
  }, [])

  const value = React.useMemo(() => ({
    ...state,
    sessionId: state.sessionId,
    isAuthenticated: !!state.user && !state.isLoading,
    applyAuthPayload,
    refreshAuth,
    openLoginModal,
  }), [state, applyAuthPayload, refreshAuth, openLoginModal])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
